import { Inbox, LoaderCircle, X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode } from 'react';
import type { Status } from './types';

export const statusLabels: Record<string, string> = {
  ACTIVE: 'Aktiv',
  INACTIVE: 'Inaktiv',
  ARCHIVED: 'Archiviert',
  READY: 'Synchronisiert',
  PENDING: 'Ausstehend',
  ERROR: 'Fehler',
  FAILED: 'Fehlgeschlagen',
  ENABLED: 'Freigegeben',
  DISABLED: 'Deaktiviert',
  NOT_CONFIGURED: 'Nicht eingerichtet',
};
export function StatusBadge({ status }: { status: Status | string }) {
  const color = ['ACTIVE', 'READY', 'ENABLED'].includes(status)
    ? 'bg-emerald-50 text-emerald-700'
    : ['ERROR', 'FAILED'].includes(status)
      ? 'bg-red-50 text-red-700'
      : ['ARCHIVED', 'DISABLED', 'NOT_CONFIGURED'].includes(status)
        ? 'bg-slate-100 text-slate-600'
        : 'bg-amber-50 text-amber-800';
  return (
    <span className={`badge ${color}`}>{statusLabels[status] ?? status.replaceAll('_', ' ')}</span>
  );
}
export function Empty({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 p-10 text-center text-sm text-muted">
      <Inbox size={28} strokeWidth={1.4} />
      {children}
    </div>
  );
}
export function Loading({ children = 'Wird geladen…' }: { children?: ReactNode }) {
  return (
    <div role="status" className="flex items-center justify-center gap-2 p-10 text-sm text-muted">
      <LoaderCircle size={18} className="motion-safe:animate-spin" />
      {children}
    </div>
  );
}
export function ErrorMessage({ error }: { error: unknown }) {
  return error ? (
    <div
      role="alert"
      className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
    >
      {error instanceof Error
        ? error.message
        : 'Die Anfrage konnte nicht abgeschlossen werden. Bitte erneut versuchen.'}
    </div>
  ) : null;
}
export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-2 text-sm text-muted">{description}</p>}
      </div>
      {actions}
    </div>
  );
}
export function Pagination({
  page,
  pageSize,
  total,
  loading,
  onPageChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  loading?: boolean;
  onPageChange: (page: number) => void;
}) {
  const last = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="list-caption">
      <span>
        {total === 0
          ? '0 Einträge'
          : `${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, total)} von ${total} Einträgen`}
      </span>
      <div className="flex items-center gap-2">
        <button
          className="button-secondary"
          disabled={loading || page <= 1}
          onClick={() => onPageChange(page - 1)}
        >
          Zurück
        </button>
        <span className="px-2">
          {page} / {last}
        </span>
        <button
          className="button-secondary"
          disabled={loading || page >= last}
          onClick={() => onPageChange(page + 1)}
        >
          Weiter
        </button>
      </div>
    </div>
  );
}
export function Drawer({
  title,
  description,
  children,
  onClose,
}: {
  title: string;
  description?: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const onCloseRef = useRef(onClose);
  const titleId = useId();
  onCloseRef.current = onClose;
  useEffect(() => {
    const element = dialog.current;
    const previous = document.activeElement;
    element?.showModal();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={dialog}
      className="drawer-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onCloseRef.current();
      }}
      onClick={(event) => {
        if (
          event.target === dialog.current &&
          event.clientX < dialog.current.getBoundingClientRect().left
        )
          onCloseRef.current();
      }}
    >
      <div className="drawer-header">
        <div>
          <h2 id={titleId} className="text-xl font-semibold">
            {title}
          </h2>
          {description && <p className="mt-1 text-sm text-muted">{description}</p>}
        </div>
        <button
          type="button"
          className="button-secondary"
          aria-label="Details schließen"
          onClick={onClose}
        >
          <X size={18} />
        </button>
      </div>
      <div className="drawer-body">{children}</div>
    </dialog>
  );
}
